import { useCallback, useEffect, useMemo, useState } from "react";
import { useMarketQuotePanels, useMarketQuotesContext } from "features/market";
import { invalidateHoldingsCache } from "features/investments/lib/holdingsCache";
import { invalidateProfileStatsCache } from "lib/profileStatsCache";
import { getPortfolioStore, usePortfolioSnapshot } from "features/investments/lib/portfolioStore";
import { fetchCryptoPrices } from "features/market";
import { buildSymbolToPriceFromQuotes, buildSymbolToVariationFromQuotes } from "features/market";
import {
    groupPositionsByPaper,
    getTickerForPriceLookup,
    getPortfolioQuotePanelsToLoad,
    getCurrencyForSymbol,
} from "features/market";
import type { PanelType } from "features/market";
import type {
    Operation,
    PositionWithAsset,
    AddOperationPayload,
    UpdateOperationPayload,
} from "types/Portfolio";

export interface UsePortfolioReturn {
    operations: Operation[];
    positions: PositionWithAsset[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    addOperation: (payload: AddOperationPayload) => Promise<{ success: boolean; error?: string }>;
    updateOperation: (
        operationId: string,
        payload: UpdateOperationPayload
    ) => Promise<{ success: boolean; error?: string }>;
    deleteOperation: (operationId: string) => Promise<{ success: boolean; error?: string }>;
}

export function usePortfolio(username: string | null): UsePortfolioReturn {
    const { requestLoad, quoteCurrencyView } = useMarketQuotesContext();
    const store = username ? getPortfolioStore(username) : null;
    const { operations, positions: rawPositions, loading, error } = usePortfolioSnapshot(username);

    const marketPanels = useMemo(
        () => getPortfolioQuotePanelsToLoad(rawPositions),
        [rawPositions]
    );
    const quotePanels = useMarketQuotePanels(marketPanels);

    useEffect(() => {
        marketPanels.forEach((panel) => requestLoad(panel));
    }, [marketPanels, requestLoad]);

    const priceMaps = useMemo(() => {
        const maps = new Map<PanelType, Map<string, number>>();
        marketPanels.forEach((panel) => {
            if (panel === "crypto") return; // crypto uses CoinGecko, not IOL quotes
            const data = quotePanels[panel]?.data;
            if (data?.quotes) {
                maps.set(panel, buildSymbolToPriceFromQuotes(data.quotes));
            }
        });
        return maps;
    }, [marketPanels, quotePanels]);

    const variationMaps = useMemo(() => {
        const maps = new Map<PanelType, Map<string, number>>();
        marketPanels.forEach((panel) => {
            if (panel === "crypto") return;
            const data = quotePanels[panel]?.data;
            if (data?.quotes) {
                maps.set(panel, buildSymbolToVariationFromQuotes(data.quotes));
            }
        });
        return maps;
    }, [marketPanels, quotePanels]);

    const cryptoIds = useMemo(() => {
        const ids = new Set<string>();
        rawPositions.forEach((p) => {
            if (p.assetId?.marketPanel === "crypto" && p.assetId?.coingeckoId) {
                ids.add(p.assetId.coingeckoId);
            }
        });
        return [...ids];
    }, [rawPositions]);

    const [cryptoPrices, setCryptoPrices] = useState<Record<string, { usd?: number; usd_24h_change?: number }>>({});
    const cryptoIdsStr = cryptoIds.slice().sort().join(",");
    useEffect(() => {
        if (cryptoIds.length === 0) {
            setCryptoPrices({});
            return;
        }
        const ids = cryptoIds.slice();
        let cancelled = false;
        fetchCryptoPrices(ids).then((res) => {
            if (cancelled) return;
            const map: Record<string, { usd?: number; usd_24h_change?: number }> = {};
            Object.entries(res).forEach(([id, data]) => {
                if (data?.usd != null || data?.usd_24h_change != null) {
                    map[id] = { usd: data.usd, usd_24h_change: data.usd_24h_change };
                }
            });
            setCryptoPrices(map);
        }).catch(() => {
            if (!cancelled) setCryptoPrices({});
        });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cryptoIdsStr is stable when ids unchanged
    }, [cryptoIdsStr]);

    const enrichedPositions = useMemo(() => {
        const lookupPrice = (
            tickersToTry: string[],
            ownPanel: PanelType | undefined,
            maps: Map<PanelType, Map<string, number>>,
        ): { price: number; ticker: string; panel: PanelType } | null => {
            const ownMap = ownPanel ? maps.get(ownPanel) : undefined;
            for (const ticker of tickersToTry) {
                const value = ownMap?.get(ticker);
                if (value != null && ownPanel) return { price: value, ticker, panel: ownPanel };
            }
            for (const ticker of tickersToTry) {
                for (const [panel, map] of maps) {
                    const value = map.get(ticker);
                    if (value != null) return { price: value, ticker, panel };
                }
            }
            return null;
        };

        return rawPositions.map((p) => {
            const asset = p.assetId;
            let currentPrice: number | null = null;
            let dailyVariation: number | null = null;
            let priceDate: string | undefined;
            let currentPriceCurrency: string | undefined;
            let priceSource: PositionWithAsset["priceSource"] = "cost";

            if (asset?.marketPanel === "crypto" && asset.coingeckoId) {
                const crypto = cryptoPrices[asset.coingeckoId];
                currentPrice = crypto?.usd ?? null;
                dailyVariation = crypto?.usd_24h_change ?? null;
                currentPriceCurrency = "USD";
                if (currentPrice != null) priceSource = "crypto";
            } else if (asset?.symbol != null) {
                const symbol = asset.symbol;
                const tickersToTry = getTickerForPriceLookup(symbol, quoteCurrencyView);
                const ownPanel = asset.marketPanel as PanelType | undefined;

                const quote = lookupPrice(tickersToTry, ownPanel, priceMaps);
                currentPrice = quote?.price ?? null;
                dailyVariation = quote
                    ? variationMaps.get(quote.panel)?.get(quote.ticker) ?? null
                    : null;
                if (quote) {
                    priceSource = "market";
                    currentPriceCurrency = getCurrencyForSymbol(quote.ticker);
                    priceDate = quotePanels[quote.panel]?.data?.updatedAt || undefined;
                }
            }
            return {
                ...p,
                currentPrice: currentPrice ?? undefined,
                currentPriceCurrency,
                priceSource,
                priceDate,
                dailyVariation: dailyVariation ?? undefined,
            };
        });
    }, [rawPositions, priceMaps, variationMaps, quoteCurrencyView, cryptoPrices, quotePanels]);

    const positions = useMemo(
        () => groupPositionsByPaper(enrichedPositions, quoteCurrencyView),
        [enrichedPositions, quoteCurrencyView]
    );

    const fetchPortfolio = useCallback(() => store?.refresh() ?? Promise.resolve(), [store]);

    const afterMutation = useCallback(async (result: { success: boolean; error?: string }) => {
        if (result.success && username) {
            invalidateHoldingsCache(username);
            invalidateProfileStatsCache(username);
        }
        return result;
    }, [username]);

    const addOperation = useCallback((payload: AddOperationPayload) => {
        if (!store) return Promise.resolve({ success: false, error: "Not logged in" });
        return store.addOperation(payload).then(afterMutation);
    }, [store, afterMutation]);

    const updateOperation = useCallback((operationId: string, payload: UpdateOperationPayload) => {
        if (!store) return Promise.resolve({ success: false, error: "Not logged in" });
        return store.updateOperation(operationId, payload).then(afterMutation);
    }, [store, afterMutation]);

    const deleteOperation = useCallback((operationId: string) => {
        if (!store) return Promise.resolve({ success: false, error: "Not logged in" });
        return store.deleteOperation(operationId).then(afterMutation);
    }, [store, afterMutation]);

    return {
        operations,
        positions,
        loading,
        error,
        refetch: fetchPortfolio,
        addOperation,
        updateOperation,
        deleteOperation,
    };
}
